// src/pages/sales/QuotationEditModal.jsx
import { useState } from "react";

const INCOTERMS = [
  { value: "EXW", label: "EXW — Ex-Warehouse" },
  { value: "DAP", label: "DAP — Delivered at Place" },
  { value: "DDP", label: "DDP — Delivered Duty Paid" },
  { value: "FOB", label: "FOB — Free on Board" },
  { value: "CFR", label: "CFR — Cost and Freight" },
  { value: "CIF", label: "CIF — Cost, Insurance & Freight" },
];

export function QuotationEditModal({ quotation: q, fgs, sfgs, hmGrades, onSave, onCancel }) {
  const companyType = q.quoNumber?.startsWith("AMIQ") ? "WB" : "HM";
  const businessUnit = companyType === "WB" ? "AMI" : "AMT";

  const products = companyType === "WB"
    ? [
        ...(fgs || []).map(f => ({ id: `FG-${f.id}`, code: String(f.id), label: `${f.id} — ${f.name || f.id}`, name: f.name || String(f.id) })),
        ...(sfgs || []).map(s => ({ id: `SFG-${s.id}`, code: String(s.id), label: `SFG: ${s.name || s.id}`, name: s.name || String(s.id) })),
      ]
    : (hmGrades || []).filter(g => g.active).map(g => ({ id: g.id, code: g.id, label: g.displayName || g.id, name: g.displayName || g.id }));

  const [form, setForm] = useState({
    customerName:  q.customerName  || "",
    shipToName:    q.shipToName    || "",
    addressedTo:   q.addressedTo   || "",
    contactPerson: q.contactPerson || "",
    reference:     q.reference     || "",
    preparedBy:    q.preparedBy    || "",
    date:          q.date          || new Date().toISOString().slice(0, 10),
    vatEnabled:    q.vatEnabled    || false,
    paymentTerms:  q.paymentTerms  || "",
    incoterm:      q.incoterm      || "",
    validityDays:  q.validityDays  || 7,
    notes:         q.notes         || "",
  });

  const [lines, setLines] = useState(
    (q.lines || []).map(l => ({
      productName: l.productName || "",
      productCode: l.productCode || "",
      quantity:    String(l.quantity),
      uom:         l.uom || "kg",
      unitPrice:   String(l.unitPrice),
      packingSize: l.packingSize || "",
      isOther:     !l.productCode,
    }))
  );

  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  function setF(key, val) { setForm(f => ({ ...f, [key]: val })); }

  function setLine(i, key, val) {
    setLines(ls => ls.map((l, idx) => idx === i ? { ...l, [key]: val } : l));
  }

  function selectProduct(i, value) {
    if (value === "__OTHER__") {
      setLines(ls => ls.map((l, idx) => idx === i
        ? { productName: "", productCode: "", quantity: l.quantity, uom: l.uom, unitPrice: l.unitPrice, packingSize: l.packingSize, isOther: true }
        : l));
      return;
    }
    const p = products.find(p => p.id === value);
    if (!p) return;
    setLines(ls => ls.map((l, idx) => idx === i
      ? { ...l, productCode: p.code, productName: p.name, isOther: false }
      : l));
  }

  function backToDropdown(i) {
    setLines(ls => ls.map((l, idx) => idx === i
      ? { ...l, productCode: "", productName: "", isOther: false }
      : l));
  }

  function addLine() {
    setLines(ls => [...ls, { productName: "", productCode: "", quantity: "", uom: "kg", unitPrice: "", packingSize: "", isOther: false }]);
  }
  function removeLine(i) { setLines(ls => ls.filter((_, idx) => idx !== i)); }

  function subtotal(line) {
    const qty = parseFloat(line.quantity), price = parseFloat(line.unitPrice);
    return (!isNaN(qty) && !isNaN(price)) ? (qty * price).toFixed(2) : "—";
  }

  function grandSubtotal() {
    return lines.reduce((sum, l) => {
      const qty = parseFloat(l.quantity), price = parseFloat(l.unitPrice);
      return sum + (!isNaN(qty) && !isNaN(price) ? qty * price : 0);
    }, 0);
  }

  function vatAmount()  { return form.vatEnabled ? grandSubtotal() * 0.05 : 0; }
  function grandTotal() { return grandSubtotal() + vatAmount(); }

  function fmt(n) {
    return n.toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  const valid =
    form.customerName.trim() &&
    form.addressedTo.trim() &&
    form.contactPerson.trim() &&
    form.preparedBy.trim() &&
    form.paymentTerms.trim() &&
    form.incoterm &&
    lines.length > 0 &&
    lines.every(l => l.productName.trim() && l.quantity && l.unitPrice);

  async function handleSave() {
    if (!valid) return;
    setSaving(true); setError(null);
    try {
      await onSave(q.id, {
        businessUnit,
        currency,
        accountId:     q.accountId || null,
        date:          form.date,
        customerName:  form.customerName.trim(),
        shipToName:    form.shipToName.trim()    || null,
        addressedTo:   form.addressedTo.trim(),
        contactPerson: form.contactPerson.trim(),
        reference:     form.reference.trim()     || null,
        preparedBy:    form.preparedBy.trim(),
        validityDays:  Number(form.validityDays),
        vatEnabled:    form.vatEnabled,
        paymentTerms:  form.paymentTerms.trim(),
        incoterm:      form.incoterm,
        notes:         form.notes.trim()         || null,
        lines: lines.map(l => ({
          productName: l.productName.trim(),
          productCode: l.productCode || null,
          quantity:    parseFloat(l.quantity),
          uom:         l.uom.trim(),
          unitPrice:   parseFloat(l.unitPrice),
          packingSize: l.packingSize?.trim() || null,
        })),
      });
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  }

  const companyLabel = companyType === "WB" ? "Industries (AMI)" : "Trading (AMT)";
  const currency     = q.currency || "AED";

  return (
    <div className="modal" style={{ width: 780, maxHeight: "90vh", overflowY: "auto" }}>
      <div className="modal-header">
        <div className="modal-title">Edit Quotation — <span style={{ fontFamily: "monospace" }}>{q.quoNumber}</span></div>
        <button className="modal-close" onClick={onCancel}>✕</button>
      </div>
      <div className="modal-body">

        {/* Company & Currency — both read-only after creation */}
        <div className="form-row" style={{ marginBottom: 14 }}>
          <span className="form-label">Company</span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: 12, background: "#e5e7eb", borderRadius: 4,
              padding: "4px 10px", color: "#374151", fontWeight: 600 }}>{companyLabel}</span>
            <span style={{ fontSize: 11, color: "#6b7280" }}>Currency:</span>
            <span style={{ fontSize: 12, background: "#e5e7eb", borderRadius: 4,
              padding: "4px 10px", color: "#374151", fontWeight: 600 }}>{currency}</span>
          </div>
        </div>

        <div className="form-sep" />

        {/* Customer info */}
        <div className="form-row">
          <span className="form-label">Customer Name *</span>
          <input className="form-input" style={{ flex: 1 }}
            value={form.customerName} onChange={e => setF("customerName", e.target.value)} />
        </div>
        <div className="form-row">
          <span className="form-label">Ship To Name</span>
          <input className="form-input" style={{ flex: 1 }}
            value={form.shipToName} onChange={e => setF("shipToName", e.target.value)} />
        </div>
        <div className="form-row">
          <span className="form-label">Kind Attn *</span>
          <input className="form-input" style={{ flex: 1 }}
            value={form.addressedTo} onChange={e => setF("addressedTo", e.target.value)} />
        </div>
        <div className="form-row">
          <span className="form-label">Contact Person *</span>
          <input className="form-input" style={{ flex: 1 }}
            value={form.contactPerson} onChange={e => setF("contactPerson", e.target.value)} />
        </div>
        <div className="form-row">
          <span className="form-label">Customer Ref</span>
          <input className="form-input" style={{ flex: 1 }}
            value={form.reference} onChange={e => setF("reference", e.target.value)} />
        </div>
        <div className="form-row">
          <span className="form-label">Prepared By</span>
          <input className="form-input" style={{ flex: 1, background: "#f3f4f6", color: "#374151" }}
            value={form.preparedBy} readOnly />
        </div>
        <div className="form-row">
          <span className="form-label">Date *</span>
          <input className="form-input" type="date" value={form.date}
            onChange={e => setF("date", e.target.value)} style={{ width: 160 }} />
        </div>

        <div className="form-sep" />

        {/* Line items */}
        <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 8 }}>
          Line Items <span style={{ fontWeight: 400, color: "#6b7280" }}>(at least one required)</span>
        </div>

        {lines.map((line, i) => (
          <div key={i} style={{ border: "1px solid #e5e7eb", borderRadius: 6,
            padding: "10px 12px", marginBottom: 8, background: "#fafafa" }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>

              <div style={{ flex: "2 1 220px" }}>
                <div style={{ fontSize: 11, color: "#555", marginBottom: 2 }}>Product *</div>
                {line.isOther ? (
                  <div style={{ display: "flex", gap: 4 }}>
                    <input className="form-input" style={{ flex: 1 }}
                      placeholder="Enter product name"
                      value={line.productName}
                      onChange={e => setLine(i, "productName", e.target.value)} />
                    <button className="btn-small" style={{ fontSize: 10, whiteSpace: "nowrap" }}
                      onClick={() => backToDropdown(i)}>← List</button>
                  </div>
                ) : (
                  <select className="form-select" style={{ width: "100%" }}
                    value={products.find(p => p.code === line.productCode)?.id ?? ""}
                    onChange={e => selectProduct(i, e.target.value)}>
                    <option value="">— Select —</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                    <option value="__OTHER__">Other (type manually)</option>
                  </select>
                )}
              </div>

              <div style={{ flex: "0 1 70px" }}>
                <div style={{ fontSize: 11, color: "#555", marginBottom: 2 }}>Qty *</div>
                <input className="form-input" type="number" min="0" step="0.001"
                  style={{ width: "100%" }} value={line.quantity}
                  onChange={e => setLine(i, "quantity", e.target.value)} />
              </div>
              <div style={{ flex: "0 1 60px" }}>
                <div style={{ fontSize: 11, color: "#555", marginBottom: 2 }}>UOM</div>
                <input className="form-input" style={{ width: "100%" }}
                  value={line.uom}
                  onChange={e => setLine(i, "uom", e.target.value)} />
              </div>
              <div style={{ flex: "1 1 100px" }}>
                <div style={{ fontSize: 11, color: "#555", marginBottom: 2 }}>Packing Size</div>
                <input className="form-input" style={{ width: "100%" }}
                  placeholder="e.g. 20 Kg Pail"
                  value={line.packingSize}
                  onChange={e => setLine(i, "packingSize", e.target.value)} />
              </div>
              <div style={{ flex: "0 1 95px" }}>
                <div style={{ fontSize: 11, color: "#555", marginBottom: 2 }}>Price ({currency}) *</div>
                <input className="form-input" type="number" min="0" step="0.0001"
                  style={{ width: "100%", textAlign: "right" }} value={line.unitPrice}
                  onChange={e => setLine(i, "unitPrice", e.target.value)} />
              </div>
              <div style={{ flex: "0 1 90px", textAlign: "right" }}>
                <div style={{ fontSize: 11, color: "#555", marginBottom: 2 }}>Subtotal</div>
                <div style={{ fontWeight: 600, fontSize: 13, padding: "6px 0" }}>{subtotal(line)}</div>
              </div>
              <div>
                {lines.length > 1 && (
                  <button className="btn-small" style={{ color: "#dc2626" }}
                    onClick={() => removeLine(i)}>✕</button>
                )}
              </div>
            </div>
          </div>
        ))}

        <button className="btn" style={{ fontSize: 12, marginBottom: 12 }}
          onClick={addLine}>+ Add Line</button>

        <div style={{ textAlign: "right", fontSize: 12, marginBottom: 12, paddingRight: 4 }}>
          <div>Sub Total: <strong>{currency} {fmt(grandSubtotal())}</strong></div>
          {form.vatEnabled && <div>VAT 5%: <strong>{currency} {fmt(vatAmount())}</strong></div>}
          <div style={{ fontSize: 14, marginTop: 2 }}>Total: <strong>{currency} {fmt(grandTotal())}</strong></div>
        </div>

        <div className="form-sep" />

        {/* Terms */}
        <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 8 }}>Terms &amp; Conditions</div>

        <div className="form-row">
          <span className="form-label">Payment Terms *</span>
          <input className="form-input" style={{ flex: 1 }}
            placeholder="e.g. 30 Days PDC on Delivery"
            value={form.paymentTerms} onChange={e => setF("paymentTerms", e.target.value)} />
        </div>

        <div className="form-row">
          <span className="form-label">Delivery (Incoterm) *</span>
          <select className="form-select" style={{ width: 280 }}
            value={form.incoterm} onChange={e => setF("incoterm", e.target.value)}>
            <option value="">— Select —</option>
            {INCOTERMS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        <div className="form-row">
          <span className="form-label">Validity (days)</span>
          <input className="form-input" type="number" min="1" max="30" style={{ width: 80 }}
            value={form.validityDays} onChange={e => setF("validityDays", e.target.value)} />
        </div>

        <div className="form-row" style={{ alignItems: "center" }}>
          <span className="form-label">VAT 5%</span>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={form.vatEnabled}
              onChange={e => setF("vatEnabled", e.target.checked)} />
            Include 5% VAT in total
          </label>
        </div>

        <div className="form-row" style={{ alignItems: "flex-start" }}>
          <span className="form-label" style={{ paddingTop: 6 }}>Notes</span>
          <textarea className="form-input" style={{ flex: 1, minHeight: 70, resize: "vertical" }}
            value={form.notes} onChange={e => setF("notes", e.target.value)} />
        </div>

        {error && <div className="alert-warn" style={{ marginTop: 8 }}>{error}</div>}

        <div className="form-sep" />
        <div className="form-footer">
          <button className="btn" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" disabled={!valid || saving}
            onClick={handleSave}>{saving ? "Saving…" : "Save Changes"}</button>
        </div>
      </div>
    </div>
  );
}
